import {
  type ActionHost,
  appOn,
  type Chat,
  type ClientState,
  type EmailAttachment,
  type Recipient,
} from "@rhema/client";
import { useMemo, useRef } from "react";
import { chatTitle, memberNames, peerOf } from "../data/view";
import { useLinks } from "../links/useLinks";
import { useClient } from "../session/session";

/** How well a chat matches the name Queravel gave. Zero means no match. */
function score(chat: Chat, title: string, handle: string | undefined, to: string): number {
  const want = to.trim().toLowerCase().replace(/^@/, "");
  const name = title.toLowerCase();
  if (!want) return 0;
  if (name === want || handle === want) return 4;
  if (name.split(/\s+/)[0] === want) return 3;
  if (name.startsWith(want)) return 2;
  if (name.includes(want) || (chat.kind === "group" && chat.about.toLowerCase().includes(want))) return 1;
  return 0;
}

/**
 * Chats on this device that match a name, best match first. Your Queravel chat never matches, and
 * neither does a group or channel whose admins turned Queravel off.
 */
export function findRecipients(state: ClientState, to: string): Recipient[] {
  const meId = state.me?.id;
  const found: (Recipient & { score: number })[] = [];
  for (const id of state.chatOrder) {
    const chat = state.chats[id];
    if (!chat || chat.archived || !appOn(state, chat, "queravel")) continue;
    const title = chatTitle(chat, meId);
    const peer = peerOf(chat, meId);
    const s = score(chat, title, peer?.handle.toLowerCase(), to);
    if (!s) continue;
    found.push({
      chatId: chat.id,
      label: title,
      detail: chat.kind === "group" ? `Group with ${memberNames(chat, meId)}` : `@${peer?.handle ?? ""}`,
      score: s,
    });
  }
  return found.sort((a, b) => b.score - a.score).map(({ score: _s, ...r }) => r);
}

/**
 * What Queravel's actions can do on this device. Sends go out as ordinary encrypted messages. Photos
 * come from Penielly to this device first. Email drafts are saved in Malakh and never sent.
 */
export function useActionHost(): ActionHost {
  const client = useClient();
  const links = useLinks();
  const latest = useRef({ client, links });
  latest.current = { client, links };

  return useMemo<ActionHost>(
    () => ({
      findRecipients: (to) => findRecipients(latest.current.client.getState(), to),
      async sendMessage(chatId, { text, photos }) {
        const { client, links } = latest.current;
        for (const item of photos) await links.inChat(chatId).penielly.sendToChat(client, chatId, item);
        if (text) await client.send(chatId, { type: "text", body: text });
      },
      searchPhotos: (params) => latest.current.links.penielly.search(params),
      async draftEmail({ to, subject, body, photos }) {
        const { links } = latest.current;
        const attachments: EmailAttachment[] = [];
        for (const item of photos) {
          const file = await links.penielly.fetchOriginal(item);
          attachments.push({ blob: file, name: file.name, type: file.type || item.mime });
        }
        await links.malakh.createDraft({ to, subject, body, attachments });
      },
    }),
    [],
  );
}
